import type { ReactElement } from 'react';
import { formatCellValue } from './dataFormatters';
import type { ChartSeriesConfig } from '@reports/domain/value-objects/blocks';

/**
 * Recharts tooltip payload item
 */
export interface TooltipPayloadItem {
  name: string;
  value: number;
  fill?: string;
  payload?: {
    fill?: string;
    name?: string;
    value?: number;
  };
}

/**
 * Creates a bar chart tooltip formatter function
 *
 * Returns a formatter that displays series label, color indicator, and formatted value.
 *
 * @param {ChartSeriesConfig} seriesConfig - Series configuration with formatting rules
 * @param {Function} t - Translation function
 * @returns {Function} Tooltip formatter function
 *
 * @example
 * const formatter = createBarTooltipFormatter(seriesConfig, t);
 * <ChartTooltip content={<ChartTooltipContent formatter={formatter} />} />
 */
export const createBarTooltipFormatter = (seriesConfig: ChartSeriesConfig, t: (key: string) => string) => {
  return (value: number, name: string | number, item: TooltipPayloadItem): ReactElement => {
    const labelSeries = t(seriesConfig.label);
    const formattedValue = formatCellValue(value?.toString(), seriesConfig.format);
    const color = item.payload?.fill || '#EB6668';

    return (
      <div className="flex items-center gap-2">
        <div
          className="h-2.5 w-2.5 shrink-0 rounded-xs"
          style={{
            backgroundColor: color,
          }}
        />
        <span className="font-normal text-muted-foreground">{labelSeries}</span>
        <span className="ml-auto font-normal text-foreground">{formattedValue}</span>
      </div>
    );
  };
};

/**
 * Creates a pie chart tooltip formatter function
 *
 * Returns a formatter that displays segment name, color indicator, and formatted value.
 *
 * @param {ChartSeriesConfig} seriesConfig - Series configuration with formatting rules
 * @returns {Function} Tooltip formatter function
 *
 * @example
 * const formatter = createPieTooltipFormatter(seriesConfig);
 * <ChartTooltip content={<ChartTooltipContent formatter={formatter} />} />
 */
export const createPieTooltipFormatter = (seriesConfig: ChartSeriesConfig) => {
  return (value: number, name: string | number, item: TooltipPayloadItem): ReactElement => {
    const formattedValue = formatCellValue(value?.toString(), seriesConfig.format);

    return (
      <div className="flex items-center gap-2">
        <div
          className="h-2.5 w-2.5 shrink-0 rounded-xs"
          style={{
            backgroundColor: item.payload?.fill || '#EB6668',
          }}
        />
        <span className="font-normal text-muted-foreground">{name}</span>
        <span className="ml-auto font-normal text-foreground">{formattedValue}</span>
      </div>
    );
  };
};
